import React from 'react';

export interface InfoBannerProps {
  icon: React.ReactNode;
  children: React.ReactNode;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  className?: string;
  height?: number;
}

/**
 * InfoBanner - A reusable banner component for displaying informational messages
 *
 * @param icon - The icon to display (should be sized appropriately, e.g., 24x24)
 * @param children - The message content
 * @param backgroundColor - Background color (default: blue-10 #EAF6FF)
 * @param borderColor - Border color (default: blue-30 #82CFFF)
 * @param textColor - Text color (default: #14151C)
 * @param className - Additional CSS classes
 */

const InfoBanner: React.FC<InfoBannerProps> = ({
  icon,
  children,
  backgroundColor = '#EAF6FF',
  borderColor = '#82CFFF',
  textColor = '#14151C',
  className = '',
  height
}) => {
  return (
    <div
      data-testid="info-banner-container"
      className={`flex items-center gap-3 ${className}`}
      style={{
        padding: '12px 16px',
        borderRadius: '4px',
        border: `1px solid ${borderColor}`,
        background: backgroundColor,
        boxSizing: 'border-box',
        ...(height && { height: `${height}px` })
      }}
    >
      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          height: '24px'
        }}
      >
        {icon}
      </div>
      <div
        style={{
          color: textColor,
          fontSize: '14px',
          lineHeight: '20px',
          flex: 1
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default InfoBanner;
